/**
 * SDK 房间详情弹窗 - 房间名点击弹出的设施 + 图片弹窗
 *
 * 复刻主流程 src/components/packages/RoomDetailModal 的内容(设施分组 + 图片),
 * 但用 SDK 自写弹层(不引 Radix),并 portal 到 document.body 级独立 shadow:
 * 组件本身在 <hotel-*> Shadow DOM 内,而客服等第三方挂件挂 body、z-index 不可控,
 * 留在原 shadow 内的遮罩无法盖住 body 级挂件;portal 出去才能在根层叠上下文盖住它们。
 *  - PC:fixed 居中 Modal(左设施 2 列网格 + 右图片纵向滚动)
 *  - H5(≤640):fixed 底部抽屉(顶图片轮播 + 下设施),复刻日历浮层开合动画
 * 数据复用 room 上已有 facilities/images(同源于 package list 接口,零接口改动)。
 *
 * 开合:isOpen=true 同帧挂载 data-state=open(进入动画);关闭时 H5 播滑出后卸载、
 *   PC 瞬时卸载(对齐主流程桌面 if(!isOpen) return null);遮罩 onClick 关闭,
 *   内容区 stopPropagation。rendering(=挂载态)与 isOpen 解耦,H5 退出动画期间维持挂载。
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { RoomType, RoomImage, FacilityGroup } from '@/types/room';
import { useMediaQuery } from '@/ui/hooks/useMediaQuery';
import { getSdkModalPortal } from '../lib/modal-portal';

interface RoomDetailModalProps {
  room: RoomType;
  isOpen: boolean;
  onClose: () => void;
}

/** 默认设施图标(复刻主流程 RoomDetailModal.DefaultFacilityIcon,icon 为空时 fallback) */
function DefaultFacilityIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12h16" />
      <path d="M4 12a8 8 0 0 1 16 0" />
      <path d="M6 12v4a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-4" />
    </svg>
  );
}

/** 关闭按钮 X 图标 */
function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** 无图片占位图标(复刻主流程占位图) */
function ImagePlaceholderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  );
}

/**
 * H5 顶部图片轮播(轻量 translateX,复刻 RoomCard 轮播模式,弹窗自包含)。
 * SDK 独立 bundle 不引 embla;RoomCard 的轮播 H5 退化为单图、与本弹窗行为不同,故不复用。
 */
function ModalImageCarousel({ images, alt }: { images: RoomImage[]; alt: string }) {
  const [imgIndex, setImgIndex] = useState(0);
  const imgs = images.slice(0, 5);
  const hasMultiple = imgs.length > 1;
  const goPrev = () => setImgIndex((i) => (i - 1 + imgs.length) % imgs.length);
  const goNext = () => setImgIndex((i) => (i + 1) % imgs.length);

  if (imgs.length === 0) {
    return (
      <div className="hh-modal__carousel hh-modal__carousel--empty">
        <ImagePlaceholderIcon />
      </div>
    );
  }

  return (
    <div className="hh-modal__carousel">
      <div
        className="hh-modal__carousel-track"
        style={{ transform: `translateX(-${imgIndex * 100}%)` }}
      >
        {imgs.map((img, i) => (
          // SDK 独立 bundle 脱离 Next 运行时,无法用 next/image,用原生 img
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={i}
            className="hh-modal__carousel-img"
            src={img.src}
            alt={img.alt || `${alt} - 图片${i + 1}`}
            draggable={false}
          />
        ))}
      </div>
      {hasMultiple && (
        <>
          <button
            type="button"
            className="hh-modal__carousel-nav hh-modal__carousel-nav--prev"
            aria-label="上一张"
            onClick={goPrev}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="hh-modal__carousel-nav hh-modal__carousel-nav--next"
            aria-label="下一张"
            onClick={goNext}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
          <div className="hh-modal__carousel-dots">
            {imgs.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`hh-modal__carousel-dot${i === imgIndex ? 'hh-modal__carousel-dot--active' : ''}`}
                aria-label={`第 ${i + 1} 张`}
                onClick={() => setImgIndex(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** 设施分组渲染(PC/H5 共用):每组标题 + 2 列网格(icon + name) */
function FacilityList({ facilities }: { facilities: FacilityGroup[] }) {
  return (
    <>
      {facilities.map((group) => (
        <div className="hh-modal__group" key={group.title}>
          <h3 className="hh-modal__group-title">{group.title}</h3>
          <div className="hh-modal__group-items">
            {group.items.map((item) => (
              <div className="hh-modal__facility" key={item.name}>
                {item.icon ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img className="hh-modal__facility-icon" src={item.icon} alt="" />
                ) : (
                  <span className="hh-modal__facility-icon hh-modal__facility-icon--default">
                    <DefaultFacilityIcon />
                  </span>
                )}
                <span className="hh-modal__facility-name">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function RoomDetailModal({ room, isOpen, onClose }: RoomDetailModalProps) {
  // 断点 640px,与 styles.h5.ts 一致。仅驱动 PC/H5 渲染分支(仅挂载时渲染,翻转不可见)。
  const isMobile = useMediaQuery('(max-width: 640px)', false);
  // 挂载/卸载与退出动画时机由父组件(RoomCard)控制:isOpen=true 进入动画;
  //   isOpen=false 时父延迟卸载(H5 播滑出)/瞬时卸载(PC,对齐主流程桌面)。
  const state = isOpen ? 'open' : 'closed';
  // 逃出组件 Shadow DOM:弹窗 portal 到 document.body 级独立 shadow,确保遮罩盖住挂 body
  //   的第三方挂件(如客服 widget.js)。mount 懒创建单例;SSR/无 document 时为 null 不渲染。
  const [portalMount] = useState(getSdkModalPortal);

  // H5 底部抽屉:遮罩 + 拖拽条 + 关闭按钮 + 标题 + 图片轮播 + 设施
  const content = isMobile ? (
    <>
      <div className="hh-modal__backdrop" data-state={state} onClick={onClose} />
      <div className="hh-modal__card hh-modal__card--sheet" data-state={state}>
        <div className="hh-modal__handle" aria-hidden="true" />
        <button type="button" className="hh-modal__close" aria-label="关闭" onClick={onClose}>
          <CloseIcon />
        </button>
        <h2 className="hh-modal__title">{room.name}</h2>
        <ModalImageCarousel images={room.images} alt={room.name} />
        <div className="hh-modal__facilities">
          <FacilityList facilities={room.facilities} />
        </div>
      </div>
    </>
  ) : (
    // PC 居中 Modal:遮罩(flex 居中) + 卡片(左设施 + 右图片)
    <div className="hh-modal__backdrop" data-state={state} onClick={onClose}>
      <div className="hh-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="hh-modal__header">
          <button type="button" className="hh-modal__close" aria-label="关闭" onClick={onClose}>
            <CloseIcon />
          </button>
          <h2 className="hh-modal__title">{room.name}</h2>
        </div>
        <div className="hh-modal__main">
          <div className="hh-modal__facilities">
            <FacilityList facilities={room.facilities} />
          </div>
          <div className="hh-modal__images">
            {room.images.length > 0 ? (
              room.images.map((img, i) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={i}
                  className="hh-modal__image"
                  src={img.src}
                  alt={img.alt || `${room.name} - 图片${i + 1}`}
                />
              ))
            ) : (
              <div className="hh-modal__image-placeholder">
                <ImagePlaceholderIcon />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return portalMount ? createPortal(content, portalMount) : null;
}
